import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClassNameProp, HBox } from '..';

/**
 * Properties used for a {@link Timer}.
 */

export interface TimerProps extends ClassNameProp {
  /**
   * The name of timer that appears on the left side of the element
   */
  timerName?: string;

  /**
   * Message that should be shown when the timer zeroes out
   */
  timeoutMessage: string;
  /**
   * The timer will count from or to this target date
   */
  targetDate: Date | null;
  /**
   * callback function which will be invoked when the timer stops
   */
  onTimerStop?: () => void;
}

/**
 * Component for displaying a timer or countdown in gray ellipse
 * @constructor
 * @return the created {@link JSX.Element}
 * @param targetDate
 * @param currentDate
 */

function calculateTimeDiff(targetDate: Date | null, currentDate: Date) {
  if (!targetDate) {
    return {
      diffSeconds: 0,
      diffMinutes: 0,
      diffHours: 0
    };
  }

  const delta = Math.abs(targetDate?.getTime() - currentDate.getTime());
  return {
    diffSeconds: Math.floor((delta / 1000) % 60),
    diffMinutes: Math.floor((delta / (1000 * 60)) % 60),
    diffHours: Math.floor(delta / (1000 * 60 * 60))
  };
}

export function Timer({
  timerName,
  timeoutMessage,
  targetDate,
  onTimerStop,
  className = ''
}: TimerProps): JSX.Element {
  const [timer, setTimer] = useState({
    seconds: 0,
    minutes: 0,
    hours: 0
  });

  const intervalId = useRef<number | undefined>();

  const interval = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    intervalId.current = setInterval(() => {
      const currentDate = new Date();
      const { diffSeconds, diffMinutes, diffHours } = calculateTimeDiff(
        targetDate,
        currentDate
      );
      setTimer(() => ({
        seconds: diffSeconds,
        minutes: diffMinutes,
        hours: diffHours
      }));

      if (
        diffSeconds === 0 &&
        diffMinutes === 0 &&
        diffHours === 0 &&
        intervalId.current
      ) {
        clearInterval(intervalId.current);
        if (onTimerStop) {
          onTimerStop();
        }
      }
    }, 1000);
  }, [targetDate, intervalId, onTimerStop]);

  useEffect(() => {
    interval();
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [interval]);

  const { seconds, minutes, hours } = timer;

  return (
    <HBox className={`ehs-timer ${className}`.trim()}>
      <div className="ovalTimer">
        {timerName && (
          <p
            className={
              targetDate ? 'innerTimerText' : 'innerTimerText stand-alone'
            }
          >
            {timerName}
          </p>
        )}
        <div className="innerTimer borderCircle" role="timer">
          {hours === 0 && minutes === 0 && seconds === 0 ? (
            timeoutMessage
          ) : (
            <span className="timeKeeper">
              {hours > 0 && `${hours}:`}
              {hours > 0 &&
                minutes === 0 &&
                `${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`}
              {minutes > 0 &&
                `${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`}
              {hours === 0 && minutes === 0 && `${('0' + seconds).slice(-2)}s`}
            </span>
          )}
        </div>
      </div>
    </HBox>
  );
}
